import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Select, FormControl,
    InputLabel, Grid, FormHelperText,
} from '@mui/material';
import API from '../../utils/Axios.utils';

const initialForm = {
    role_title: '',
    client_id: '',
    department: '',
    no_of_people: 1,
    hiring_manager_name: '',
    comp_band_min: '',
    comp_band_max: '',
    priority: 'Standard',
    notes: '',
};

const CreateRequisitionModal = ({ open, onClose, clients, editData, onCreated, setAlert, setAlertContent, setAlertType }) => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            if (editData) {
                setForm({
                    role_title: editData.role_title || '',
                    client_id: editData.client_id || '',
                    department: editData.department || '',
                    no_of_people: editData.no_of_people || 1,
                    hiring_manager_name: editData.hiring_manager_name || '',
                    comp_band_min: editData.comp_band_min || '',
                    comp_band_max: editData.comp_band_max || '',
                    priority: editData.priority || 'Standard',
                    notes: editData.notes || '',
                });
            } else {
                setForm(initialForm);
            }
            setErrors({});
        }
    }, [open, editData]);

    const validate = () => {
        const e = {};
        if (!form.role_title.trim()) e.role_title = 'Role Title is required.';
        if (!form.department.trim()) e.department = 'Department is required.';
        if (!form.no_of_people || form.no_of_people < 1) e.no_of_people = 'Min 1 person required.';
        return e;
    };

    const handleChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleSubmit = async () => {
        const e = validate();
        if (Object.keys(e).length > 0) { setErrors(e); return; }
        setLoading(true);
        try {
            if (editData) {
                await API.put(`/api/requisitions/${editData.id}`, form);
                setAlertContent('Requisition updated successfully!');
            } else {
                await API.post('/api/requisitions/create', form);
                setAlertContent('Requisition created successfully!');
            }
            setAlertType('success');
            setAlert(true);
            setForm(initialForm);
            setErrors({});
            onCreated();
            onClose();
        } catch (err) {
            setAlertContent(err?.response?.data?.message || `Failed to ${editData ? 'update' : 'create'} requisition.`);
            setAlertType('error');
            setAlert(true);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = (event, reason) => {
        if (reason && reason === 'backdropClick') return;
        setForm(initialForm);
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{editData ? `Edit Requisition — ${editData.requisition_id}` : 'Create New Requisition'}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                        <TextField
                            label="Role Title *"
                            fullWidth
                            size="small"
                            value={form.role_title}
                            onChange={handleChange('role_title')}
                            error={!!errors.role_title}
                            helperText={errors.role_title}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Client</InputLabel>
                            <Select value={form.client_id} label="Client" onChange={handleChange('client_id')}>
                                <MenuItem value=""><em>None</em></MenuItem>
                                {(clients || []).map((c) => (
                                    <MenuItem key={c.client_id} value={c.client_id}>{c.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Department *"
                            fullWidth
                            size="small"
                            value={form.department}
                            onChange={handleChange('department')}
                            error={!!errors.department}
                            helperText={errors.department}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Number of People *"
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={form.no_of_people}
                            onChange={handleChange('no_of_people')}
                            error={!!errors.no_of_people}
                            helperText={errors.no_of_people}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Hiring Manager Name"
                            fullWidth
                            size="small"
                            value={form.hiring_manager_name}
                            onChange={handleChange('hiring_manager_name')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Min Salary"
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={form.comp_band_min}
                            onChange={handleChange('comp_band_min')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Max Salary"
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{ min: 0 }}
                            value={form.comp_band_max}
                            onChange={handleChange('comp_band_max')}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Priority</InputLabel>
                            <Select value={form.priority} label="Priority" onChange={handleChange('priority')}>
                                <MenuItem value="Standard">Standard</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                            </Select>
                            <FormHelperText>High priority items are highlighted for faster approval.</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Justification / Notes"
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            value={form.notes}
                            onChange={handleChange('notes')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Saving...' : editData ? 'Update Requisition' : 'Submit Requisition'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateRequisitionModal;
